import { checkErrors, plateMask } from "utils/forms-utils";
import { LOCALE_PT_BR } from "constants/date-time";
import { ViolationStatus } from "services/trafficViolationService";

export function validateAitDate(valuesArg, ids, messageKey) {
  const values = valuesArg.values ?? valuesArg;

  const aitDate = values[ids.aitDate].value;
  if (!aitDate) return "system.messages.errors.required-field";

  if (new Date(aitDate) > new Date()) {
    return messageKey ?? "traffic-violation.messages.errors.ait-date-limit";
  }
}

export function validateParameterizedDate(valuesArg, ids, messageKey, fieldId, isRequired = true) {
  const values = valuesArg.values ?? valuesArg;

  const aitDate = values[fieldId]?.value;

  if (!aitDate) {
    return isRequired ? "system.messages.errors.required-field" : undefined;
  }

  if (new Date(aitDate) > new Date()) {
    return messageKey ?? "traffic-violation.messages.errors.ait-date-limit";
  }
}

export function validateAitTime(valuesArg, ids, messageKey) {
  const values = valuesArg.values ?? valuesArg;
  const timeValue = values[ids.aitTime].value;
  if (!timeValue) return "system.messages.errors.required-field";

  const dateValue = values[ids.aitDate].value;
  if (!dateValue) return;

  const [hour, min] = timeValue.split(":").map(Number);
  const [year, month, day] = dateValue.split("-").map(Number);

  const aitDate = new Date(year, month - 1, day, hour, min); // Month is 0-indexed
  const today = new Date();

  if (aitDate.getDate() === today.getDate() && aitDate > today) {
    return messageKey ?? "traffic-violation.messages.errors.ait-time-limit";
  }
}

export function isAitValidated(status) {
  return status === ViolationStatus.VALIDATED.value;
}

export function isAitInvalidated(status) {
  return status !== ViolationStatus.VALIDATED.value && status !== ViolationStatus.NOT_VALIDATED.value;
}

export function isAitValidationRequired(status) {
  return status === ViolationStatus.NOT_VALIDATED.value;
}

export function getAitStatusTranslationKey(status) {
  const labelKey = Object.values(ViolationStatus).find(el => el.value === status).labelKey;
  return labelKey ? `traffic-violation.labels.status.${labelKey}` : "";
}

export function getDateTimeFromServer(serverDate) {
  if (!serverDate) return {};
  const [date, time] = serverDate.split("T");
  return { aitDate: date, aitTime: time?.slice(0, 5) };
}

export function toServerDate(date) {
  if (!date) return;
  const aitDate = new Date(date + "T00:00:00");
  return aitDate.toLocaleDateString(LOCALE_PT_BR, { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function fromServerDate(date) {
  if (!date) return;
  const [dia, mes, ano] = date.split("/").map(Number);
  return new Date(ano, mes - 1, dia).toISOString().split("T")[0];
}

/**
 * Field ids that must have their value normalized to uppercase on input.
 * Add any other field id here to get the same behavior across forms using handleAitInputChange.
 */
export const UPPERCASE_FIELD_IDS = new Set(["ait-location", "ait-reference", "ait-observations"]);

export function handleAitInputChange(event, ids, formData, setFormData, inputError, setInputError) {
  const id = event.target.id;
  let value = event.target.value;

  if (id === ids.vehiclePlate) {
    value = plateMask(value);
  }

  if (UPPERCASE_FIELD_IDS.has(id)) {
    value = value.toUpperCase();
  }

  const newFormData = { ...formData, [id]: { ...formData[id], value: value } };
  setFormData(newFormData);

  // aitTime is co-dependant of aitDate
  if (id === ids.aitDate && newFormData[ids.aitTime].value) {
    const newInputError = {
      ...inputError,
      [ids.aitDate]: validateAitDate(newFormData, ids),
      [ids.aitTime]: validateAitTime(newFormData, ids),
    };

    return setInputError(newInputError);
  }

  setInputError(checkErrors({ formData: newFormData, inputError, name: id }));
}

export function getSneDate(ait: any) {
  return ait?.data_adesao_sne_ait || ait?.data_adesao_sne_autuacao || ait?.data_adesao_sne_penalidade;
}

export const MEASUREMENT_EQUIPMENT_TYPES = {
  ETILOMETRO: "ETILOMETRO",
};

export function normalizeImagePath(path = "") {
  if (!path) return "";
  return path.split(/[/\\]/).pop();
}

export function isRemoteUrl(path) {
  if (!path || typeof path !== 'string') return false;
  return path.startsWith('http://') || path.startsWith('https://');
}

/**
 * Builds the display label for an AIT type in lists: "codigo-desdobramento - descricao".
 * Used only for display; submission still uses id_tipo_infracao (id).
 */
export function formatAitTypeDisplayLabel(item) {
  if (!item) return "";
  const code = item.codigo != null ? item.codigo : "";
  const unfold = item.desdobramento != null ? `-${item.desdobramento}` : "";
  const desc = item.descricao ?? "";
  return `${code}${unfold} - ${desc}`.trim();
}
